import React, { useState, useCallback, useMemo } from "react";
import { Camera, Folder, ArrowLeft, Check, X } from "lucide-react";

const ColorPalette = ({ initialColor = "#4DB6AC", onColorChange, onImageChange, onClose, currentImage = null }) => {
    const [selectedColor, setSelectedColor] = useState(initialColor);
    const [selectedImage, setSelectedImage] = useState(currentImage);

    // Expanded color palette with 48 colors
    const colors = useMemo(() => [
        "#EF4444", "#DC2626", "#B91C1C", // Reds
        "#F97316", "#EA580C", "#C2410C", // Oranges
        "#F59E0B", "#D97706", "#B45309", // Ambers
        "#EAB308", "#CA8A04", "#A16207", // Yellows
        "#84CC16", "#65A30D", "#4D7C0F", // Limes
        "#10B981", "#059669", "#047857", // Emeralds
        "#14B8A6", "#0D9488", "#0F766E", // Teals
        "#06B6D4", "#0891B2", "#0E7490", // Cyans
        "#0EA5E9", "#0284C7", "#0369A1", // Sky
        "#3B82F6", "#2563EB", "#1D4ED8", // Blues
        "#6366F1", "#4F46E5", "#4338CA", // Indigos
        "#8B5CF6", "#7C3AED", "#6D28D9", // Violets
        "#A855F7", "#9333EA", "#7E22CE", // Purples
        "#D946EF", "#C026D3", "#A21CAF", // Fuchsias
        "#EC4899", "#DB2777", "#BE185D", // Pinks
        "#6B7280", "#4B5563", "#374151", // Grays
    ], []);

    const handleColorSelect = useCallback((color) => {
        setSelectedColor(color);
        onColorChange?.(color);
    }, [onColorChange]);

    const handleImageSelect = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Veuillez sélectionner une image valide.");
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert("L'image ne doit pas dépasser 10MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setSelectedImage(reader.result);
            onImageChange?.(reader.result);
        };
        reader.readAsDataURL(file);
    }, [onImageChange]);

    const handleRemoveImage = useCallback(() => {
        setSelectedImage(null);
        onImageChange?.(null);
    }, [onImageChange]);

    return (
        <div className="flex flex-col h-full min-h-0 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
            {/* Header */}
            <div className="flex-shrink-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex items-center justify-between p-4">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-sm sm:text-base font-medium">Retour</span>
                    </button>

                    <h1 className="absolute left-1/2 -translate-x-1/2 font-semibold text-base sm:text-lg text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        Miniature
                    </h1>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 lg:px-8 py-6 pb-4"
                 style={{WebkitOverflowScrolling: 'touch'}}>
                {/* Preview Section */}
                <div className="flex flex-col items-center mb-8">
                    <div className="relative mb-4 group">
                        <div
                            className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-2xl shadow-xl transition-all duration-300 group-hover:shadow-2xl group-hover:scale-105"
                            style={{ backgroundColor: selectedColor }}
                        >
                            {selectedImage ? (
                                <>
                                    <button
                                        onClick={handleRemoveImage}
                                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
                                        aria-label="Remove image"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                    <img
                                        src={selectedImage}
                                        alt="Selected"
                                        className="w-full h-full object-cover rounded-2xl"
                                    />
                                </>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Camera className="h-10 w-10 sm:h-12 sm:w-12 text-white opacity-60 transition-opacity group-hover:opacity-80" />
                                </div>
                            )}
                        </div>
                        {/* Color indicator ring */}
                        {!selectedImage && (
                            <div
                                className="absolute inset-0 rounded-2xl ring-4 ring-offset-4 transition-all opacity-0 group-hover:opacity-100"
                                style={{ ringColor: selectedColor }}
                            />
                        )}
                    </div>

                    <p className="text-sm text-gray-600 text-center max-w-xs">
                        Choisissez une couleur d'arrière-plan ou importez votre propre image
                    </p>
                </div>

                {/* Color Grid */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Palette de couleurs
                        </h3>
                        <span className="text-xs text-gray-500">
                            {colors.length} couleurs
                        </span>
                    </div>

                    <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2 sm:gap-3 max-w-4xl mx-auto">
                        {colors.map((color, index) => (
                            <button
                                key={`${color}-${index}`}
                                className="relative aspect-square rounded-xl transition-all hover:scale-110 hover:z-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-md hover:shadow-lg"
                                style={{ backgroundColor: color }}
                                onClick={() => handleColorSelect(color)}
                                aria-label={`Select color ${color}`}
                            >
                                {selectedColor === color && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded-xl">
                                        <Check className="w-5 h-5 text-white drop-shadow-lg" strokeWidth={3} />
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Image Upload Section */}
                <div className="max-w-md mx-auto">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 text-center">
                        Image personnalisée
                    </h3>

                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="choose-image"
                    />

                    <label
                        htmlFor="choose-image"
                        className="flex items-center justify-center w-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary rounded-xl py-6 px-6 cursor-pointer hover:bg-primary/5 transition-all group"
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                                <Camera className="h-6 w-6 text-primary" />
                            </div>
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors">
                                Prendre une photo ou choisir une image
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                PNG, JPG, GIF • Max 10MB
                            </span>
                        </div>
                    </label>
                </div>
            </div>

            {/* Action Buttons — always visible at bottom */}
            <div className="flex-shrink-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg lg:hidden">
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-md"
                    >
                        Confirmer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ColorPalette;
